import React, { useCallback, useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { defaultBoostThreshold } from 'helpers/constants';
import { chartLegendItemClick, calculateDynamicChartHeight } from '../../../helpers';
import { chartHeightConstants } from '../../../constants';
import {
    PRINT_FONT_FAMILY,
    collectSeriesVisibility,
    collectYAxisVisibility,
    getVisibleSignalAxes,
    updateOppositeAxis,
    anySeriesInAxisVisible,
    getACS1000Series,
} from './helpers';
import ACS1000CoolingTooltip from './ACS1000CoolingTooltip';
import { getXAxisLabelDateFormatForDriveAnomaly } from '../helpers';

// chart configuration

const getACS1000BaseChartConfig = (title, assetDetails) => ({
    title: { text: null },
    chart: {
        events: {
            beforePrint: function () {
                if (this.exportSVGElements?.length > 0) {
                    this.exportSVGElements[0]?.hide();
                }
                this._renderEventDisabled = true;
                this.setTitle({
                    text: renderToStaticMarkup(
                        <span>{`${assetDetails?.details?.assetName} - ${title}`}</span>
                    ),
                    align: 'left',
                    x: 50,
                    y: -10,
                });
                if (this.xAxis?.length > 0) {
                    const xLabels = this.xAxis[0]?.labelGroup?.element;
                    if (xLabels?.style) { xLabels.style.fontFamily = PRINT_FONT_FAMILY; }
                }
                this.yAxis?.forEach(axis => {
                    const yLabels = axis.labelGroup?.element;
                    if (yLabels?.style) { yLabels.style.fontFamily = PRINT_FONT_FAMILY; }
                });
                const legendEl = this.legend?.group?.element;
                if (legendEl?.style) { legendEl.style.fontFamily = PRINT_FONT_FAMILY; }
            },
            afterPrint: function () {
                if (this.exportSVGElements?.length > 0) {
                    this.exportSVGElements[0]?.show();
                }
                this.setTitle({ text: '' });
                this._renderEventDisabled = false;
                this.redraw();
            },
        },
        style: { fontFamily: PRINT_FONT_FAMILY },
        spacingTop: 70,
        spacingRight: 25,
    },
    boost: { allowForce: false },
    time: { useUTC: false },
    enableNoDataZoom: true,
    navigator: { enabled: true },
    xAxis: {
        type: 'datetime',
        id: 'x',
        dateTimeLabelFormats: {
            millisecond: '%H:%M:%S',
            day: '%e. %b',
            month: '%b \'%y',
        },
        crosshair: { enabled: true, width: 2, color: colors.tundora },
        labels: {
            rotation: 50,
            color: colors.black,
            style: { fontWeight: 'bold', color: colors.black },
            formatter: function () {
                return getXAxisLabelDateFormatForDriveAnomaly(this.value);
            },
        },
        lineColor: colors.black,
        lineWidth: 2,
        tickPixelInterval: 25,
        tickColor: colors.black,
        tickWidth: 2,
    },
    legend: {
        useHTML: true,
        layout: 'horizontal',
        align: 'right',
        itemStyle: { fontFamily: PRINT_FONT_FAMILY, fontSize: '14px' },
        itemDistance: 16,
        itemMarginTop: 10,
        symbolRadius: 0,
        squareSymbol: false,
        symbolHeight: 8,
        symbolWidth: 8,
    },
    credits: { enabled: false },
    tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        shadow: false,
        padding: 0,
        borderWidth: 0,
        positioner: function (labelWidth, labelHeight, point) {
            const chart = this.chart;
            let x = point.plotX + chart.plotLeft;
            let y = point.plotY + chart.plotTop - labelHeight - 16;
            x = Math.min(x, chart.plotLeft + chart.plotWidth - labelWidth);
            y = Math.max(chart.plotTop, y);
            return { x, y };
        },
        formatter: function () {
            return renderToStaticMarkup(
                <ACS1000CoolingTooltip
                    points={this.points?.filter(
                        point => point?.series?.options?.showInLegend !== false
                    )}
                    timeStamp={this.x}
                />
            );
        },
    },
    plotOptions: {
        series: {
            turboThreshold: 0,
            animation: false,
            boostThreshold: 1000,
            findNearestPointBy: 'x',
        },
        scatter: { marker: { enabled: true }, lineWidth: 0 },
        column: {
            boostThreshold: 1000,
            groupPadding: 0,
            pointPadding: 0,
            borderWidth: 0,
            pointPlacement: 1,
        },
    },
    navigation: {
        buttonOptions: { align: 'right' },
        menuStyle: {
            borderColor: colors.white,
            boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
            borderRadius: '6px',
        },
        menuItemStyle: { padding: '6px 12px', fontSize: '14px', fontFamily: PRINT_FONT_FAMILY },
        menuItemHoverStyle: { backgroundColor: colors.gallery, color: colors.mineShaft },
    },
    lang: {
        printChart: translate('ABB.Powertrain.Frontend.printChart'),
        downloadPNG: translate('ABB.Powertrain.Frontend.saveAsImage'),
        noData: renderToStaticMarkup(
            <ChartNoDataText
                title={translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}
                text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')}
            />
        ),
    },
    noData: { useHTML: true },
    exporting: {
        enabled: true,
        scale: 1.5,
        sourceWidth: 1100,
        sourceHeight: 500,
        fallbackToExportServer: false,
        allowHTML: true,
        printMaxWidth: 1100,
        buttons: {
            contextButton: {
                enabled: false,
                menuItems: ['downloadPNG', 'printChart'],
                verticalAlign: 'top',
                x: 0,
                y: -70,
            },
        },
        chartOptions: {
            legend: {
                useHTML: false,
                squareSymbol: true,
                symbolRadius: 0,
                symbolHeight: 10,
                symbolWidth: 10,
            },
            title: {
                text: `${assetDetails?.details?.assetName} - ${title}`,
                align: 'left',
                x: 50,
                y: -10,
                style: { fontFamily: 'Verdana, sans-serif', fontWeight: 'bold' },
            },
            xAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } },
            yAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } },
        },
    },
});


const MAX_VISIBLE_SIGNALS = 5;

const syncAxisFromChart = (chart, axis) => {
    const liveAxis = chart?.get(axis?.id);
    if (!liveAxis) { return axis; }
    return { ...axis, visible: liveAxis.visible, opposite: liveAxis.opposite };
};

// Sync a config-series entry with live visibility from the chart instance
const syncSeriesFromChart = (chart, s) => {
    const liveSeries = chart?.series?.find(cs => cs?.options?.id === s?.id);
    if (!liveSeries) { return s; }
    return { ...s, visible: liveSeries.visible };
};

// Hides the oldest visible series to enforce MAX_VISIBLE_SIGNALS limit
const enforceMaxVisibleSignals = (chart, visibleSignalsQueue) => {
    while (visibleSignalsQueue.length > MAX_VISIBLE_SIGNALS) {
        const oldestId = visibleSignalsQueue.shift();
        const oldestSeries = chart.get(oldestId);
        if (oldestSeries?.visible) {
            oldestSeries.setVisible(false, false);
            const yAxisToHide = chart.get(oldestSeries.options.yAxis);
            if (yAxisToHide) { yAxisToHide.update({ visible: false }, false); }
            break;
        }
    }
};

// Collects current visibility state from the live chart and writes it back to state setters
const syncChartStateToReact = (chart, chartConfig, setSeriesVisibility, setYAxisVisibility, setYAxisOpposite, setOptions) => {
    chartConfig.yAxis = chartConfig.yAxis.map(axis => syncAxisFromChart(chart, axis));
    chartConfig.series = chartConfig.series.map(s => syncSeriesFromChart(chart, s));

    const newSeriesVis = {};
    const newYAxisVis = {};
    const newYAxisOpp = {};
    chart.series.forEach(s => collectSeriesVisibility(s, newSeriesVis));
    chart.yAxis.forEach(a => collectYAxisVisibility(a, newYAxisVis, newYAxisOpp));
    setSeriesVisibility(newSeriesVis);
    setYAxisVisibility(newYAxisVis);
    setYAxisOpposite(newYAxisOpp);

    chart.redraw();
    setOptions({ ...chartConfig });
};

const useChartConfig = (
    coolingAnomalyKPI,
    title,
    assetDetails,
    { chartInstance, chartHeight, containerRef } = {}
) => {
    const [options, setOptions] = useState({});
    const [seriesVisibility, setSeriesVisibility] = useState({});
    const [yAxisVisibility, setYAxisVisibility] = useState({});
    const [yAxisOpposite, setYAxisOpposite] = useState({});
    const [navigatorExtremes, setNavigatorExtremes] = useState(null);
    const visibleSignalsQueueRef = useRef([]);

    const conditionTrendsData = coolingAnomalyKPI?.conditionTrends;

    const handleSetExtremes = useCallback((e) => {
        if (e.trigger === 'navigator') {
            setNavigatorExtremes({ min: e.min, max: e.max, trigger: e.trigger });
        }
    }, []);

    const captureCurrentStates = useCallback(() => {
        const captured = { seriesVis: {}, yAxisVis: {}, yAxisOpp: {} };
        if (!chartInstance) { return captured; }
        chartInstance.series?.forEach(s => collectSeriesVisibility(s, captured?.seriesVis));
        chartInstance.yAxis?.forEach(a => collectYAxisVisibility(a, captured?.yAxisVis, captured?.yAxisOpp));
        setSeriesVisibility(captured?.seriesVis);
        setYAxisVisibility(captured?.yAxisVis);
        setYAxisOpposite(captured?.yAxisOpp);
        return captured;
    }, [chartInstance]);

    useEffect(() => {
        if (navigatorExtremes && chartInstance) {
            chartInstance.xAxis?.[0]?.update({
                min: navigatorExtremes?.min,
                max: navigatorExtremes?.max,
            });
        }
    }, [navigatorExtremes, chartInstance]);

    useEffect(() => {
        if (!chartInstance?.series?.length) { return; }
        chartInstance.series.forEach(series => {
            if (seriesVisibility[series?.options?.id] !== undefined) {
                series.setVisible(seriesVisibility[series?.options?.id], false);
            }
        });
        chartInstance.yAxis.forEach(axis => {
            const updates = {};
            if (yAxisVisibility[axis?.options?.id] !== undefined) {
                updates.visible = yAxisVisibility[axis?.options?.id];
            }
            if (yAxisOpposite[axis?.options?.id] !== undefined) {
                updates.opposite = yAxisOpposite[axis?.options?.id];
            }
            if (Object.keys(updates).length) { axis.update(updates, false); }
        });
        chartInstance.redraw();
    }, [chartInstance, seriesVisibility, yAxisVisibility, yAxisOpposite]);

    useEffect(() => {
        const { seriesVis, yAxisVis, yAxisOpp } = captureCurrentStates();

        const { yAxis, seriesData, allSeriesLength } =
            getACS1000Series(conditionTrendsData, chartInstance);

        const updatedSeriesData = seriesData.map(s => {
            if (seriesVis[s?.id] === undefined) { return s; }
            return { ...s, visible: seriesVis[s?.id] };
        });

        const updatedYAxis = yAxis.map(axis => {
            const updates = { ...axis };
            if (yAxisVis[axis?.id] !== undefined) { updates.visible = yAxisVis[axis?.id]; }
            if (yAxisOpp[axis?.id] !== undefined) { updates.opposite = yAxisOpp[axis?.id]; }
            return updates;
        });

        const chartConfig = getACS1000BaseChartConfig(title, assetDetails);

        chartConfig.xAxis = {
            ...chartConfig.xAxis,
            events: { afterSetExtremes: handleSetExtremes },
        };
        chartConfig.yAxis = updatedYAxis;
        chartConfig.series = updatedSeriesData;
        chartConfig.plotOptions.series.boostThreshold =
            allSeriesLength > defaultBoostThreshold ? 1 : 0;

        // Populate FIFO queue with initially visible signal IDs (only on first load)
        if (visibleSignalsQueueRef.current.length === 0) {
            visibleSignalsQueueRef.current = updatedSeriesData
                .filter(s => s.visible && s.showInLegend !== false)
                .map(s => s.id)
                .filter(Boolean);
        }
        const containerHeight = containerRef?.current?.clientHeight;
        const resolvedHeight = chartHeight ?? calculateDynamicChartHeight(
            conditionTrendsData,
            true,
            containerHeight || chartHeightConstants.default
        );
        chartConfig.chart = { ...chartConfig.chart, height: resolvedHeight };

        chartConfig.plotOptions.series.events = {
            legendItemClick: function (e) {
                const chart = this.chart;
                const clickedId = this.options?.id;

                if (!this.visible && this.options?.showInLegend !== false) {
                    // Enabling a signal: add to FIFO queue, enforce MAX_VISIBLE_SIGNALS
                    if (!visibleSignalsQueueRef.current.includes(clickedId)) {
                        visibleSignalsQueueRef.current = [...visibleSignalsQueueRef.current, clickedId];
                    }
                    enforceMaxVisibleSignals(chart, visibleSignalsQueueRef.current);
                } else if (this.visible) {
                    // Disabling: remove from queue
                    visibleSignalsQueueRef.current = visibleSignalsQueueRef.current.filter(id => id !== clickedId);
                }

                chartLegendItemClick(e);

                const yAxisId = this.options.yAxis;
                const yAxis = chart?.get(yAxisId) || this.yAxis;

                setTimeout(() => {
                    if (!yAxis) { return; }
                    const anyVisible = anySeriesInAxisVisible(chart, yAxisId, yAxis);
                    yAxis.update({ visible: anyVisible }, false);
                    getVisibleSignalAxes(chart).forEach(updateOppositeAxis);
                    syncChartStateToReact(chart, chartConfig, setSeriesVisibility, setYAxisVisibility, setYAxisOpposite, setOptions);
                }, 0);

                return true;
            },
        };

        setOptions({ ...chartConfig });
    }, [
        conditionTrendsData,
        chartInstance,
        chartHeight,
        captureCurrentStates,
        title,
    ]);

    return options;
};

export default useChartConfig;
